// src/pages/cadastros/Maquinas/NovaMaquina.jsx
// OBS: Nesta versão, "Máquinas" foi renomeado para "Equipamentos" na UI,
// mas mantemos a coleção "maquinas" para compatibilidade.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useTheme } from "../../../contexts/ThemeContext";
import { useAuth } from "../../../contexts/AuthContext";
import { useEmpresa } from "../../../contexts/EmpresaContext";
import { useIsMobile } from "../../../hooks/useIsMobile";

import LayoutDesktop from "../../../layouts/LayoutDesktop";
import LayoutMobile from "../../../layouts/LayoutMobile";

import { db } from "../../../config/firebase";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  runTransaction,
  serverTimestamp,
  where,
  writeBatch,
} from "firebase/firestore";

import { uploadToStorage } from "../../../utils/storageUpload";
import { calcularProximaDataCamadas } from "../../../utils/preventivaCamadas";

const TIPO_OPTIONS = [
  "Pin Load",
  "Plate Load",
  "Banco",
  "Cross",
  "Guiado",
  "Livre",
  "Esteira",
  "Bike",
  "Spinning",
  "Eliptico",
  "Escada",
  "Remo",
];

function uid() {
  return `${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

const PERIODICIDADE_ORDEM = ["QUINZENAL", "MENSAL", "TRIMESTRAL", "SEMESTRAL"];

function normalizarPeriodicidade(value) {
  const v = String(value || "").trim().toUpperCase();
  return PERIODICIDADE_ORDEM.includes(v) ? v : "";
}

function inferirPeriodicidadeItens(itens) {
  if (!Array.isArray(itens) || itens.length === 0) return "";
  let maxIdx = -1;
  itens.forEach((item) => {
    const camada = normalizarPeriodicidade(item?.periodicidade);
    const idx = PERIODICIDADE_ORDEM.indexOf(camada);
    if (idx > maxIdx) maxIdx = idx;
  });
  return maxIdx >= 0 ? PERIODICIDADE_ORDEM[maxIdx] : "";
}

function montarNomePreventiva(baseNome, periodicidade) {
  const nome = String(baseNome || "").trim();
  const per = String(periodicidade || "").trim();
  if (!per) return nome;
  const upperNome = nome.toUpperCase();
  const upperPer = per.toUpperCase();
  if (upperNome.includes(upperPer)) return nome;
  return `${nome} ${per}`.trim();
}

function NovaMaquina() {
  const { tema, alterarTema } = useTheme();
  const { usuario } = useAuth();
  const { modoGlobal, empresaAtual } = useEmpresa();
  const navigate = useNavigate();
  const isMobile = useIsMobile(900);

  // -------------------- estados --------------------
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState("");

  const [nome, setNome] = useState("");
  const [tipo, setTipo] = useState("");
  const [marca, setMarca] = useState("");
  const [modelo, setModelo] = useState("");
  const [numeroSerie, setNumeroSerie] = useState("");
  const [cor, setCor] = useState("");
  const [instaladoEm, setInstaladoEm] = useState(""); // YYYY-MM-DD
  const [montadoPor, setMontadoPor] = useState("");
  const [condicoesIniciais, setCondicoesIniciais] = useState(3);
  const [descricao, setDescricao] = useState("");

  // Fotos (modelo igual NovaOS: câmera + arquivo + preview + remover)
  const inputFotoCameraRef = useRef(null);
  const inputFotoArquivoRef = useRef(null);
  const [fotos, setFotos] = useState([]); // [{id, kind:'new', file, previewUrl}]

  // Cliente (somente se modoGlobal)
  const [clientes, setClientes] = useState([]);
  const [clienteId, setClienteId] = useState("");


// Academias (coleção "setores") vinculadas ao cliente selecionado
const [academias, setAcademias] = useState([]);
const [academiaId, setAcademiaId] = useState("");
const [carregandoAcademias, setCarregandoAcademias] = useState(false);

  // Modelos de Preventiva (para vincular ao equipamento)
  const [modelosPreventiva, setModelosPreventiva] = useState([]);
  const [carregandoModelosPreventiva, setCarregandoModelosPreventiva] = useState(false);
  const [filtroModeloPreventiva, setFiltroModeloPreventiva] = useState("");
  const [modeloPreventivaIds, setModeloPreventivaIds] = useState([]); // ids selecionados

  // Checklists de instalacao
  const [checklistsInstalacao, setChecklistsInstalacao] = useState([]);
  const [carregandoChecklistsInstalacao, setCarregandoChecklistsInstalacao] = useState(false);
  const [checklistInstalacaoAtiva, setChecklistInstalacaoAtiva] = useState(false);
  const [checklistInstalacaoId, setChecklistInstalacaoId] = useState("");


  const precisaSelecionarCliente = useMemo(() => {
    if (!usuario) return false;
    return !!modoGlobal;
  }, [modoGlobal, usuario]);


  const empresaIdParaAcademias = useMemo(() => {
    if (!usuario) return "";
    if (!modoGlobal && empresaAtual?.id) return empresaAtual.id;
    if (precisaSelecionarCliente) return clienteId || "";
    return "";
  }, [usuario, modoGlobal, empresaAtual, precisaSelecionarCliente, clienteId]);


  useEffect(() => {
    async function carregarClientes() {
      if (!usuario) return;
      if (!precisaSelecionarCliente) return;

      try {
        const ref = collection(db, "empresas");
        const q = query(ref, where("grupoId", "==", usuario.grupoId));
        const snap = await getDocs(q);
        const lista = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) =>
            String(a.nome || "").localeCompare(String(b.nome || ""), "pt-BR")
          );
        setClientes(lista);
      } catch (e) {
        console.error("Erro ao carregar clientes:", e);
      }
    }

    carregarClientes();
  }, [usuario, precisaSelecionarCliente]);


useEffect(() => {
  async function carregarAcademias() {
    if (!usuario?.grupoId) return;

    if (!empresaIdParaAcademias) {
      setAcademias([]);
      setAcademiaId("");
      return;
    }

    setCarregandoAcademias(true);
    try {
      const ref = collection(db, "setores");
      const q = query(
        ref,
        where("grupoId", "==", usuario.grupoId),
        where("empresaId", "==", empresaIdParaAcademias)
      );
      const snap = await getDocs(q);
      const lista = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) =>
          String(a.nome || "").localeCompare(String(b.nome || ""), "pt-BR")
        );

      setAcademias(lista);
      setAcademiaId((prev) => (lista.some((x) => x.id === prev) ? prev : ""));
    } catch (e) {
      console.error("Erro ao carregar academias:", e);
    } finally {
      setCarregandoAcademias(false);
    }
  }

  carregarAcademias();
}, [usuario, empresaIdParaAcademias]);


  useEffect(() => {
    async function carregarModelosPreventiva() {
      if (!usuario?.grupoId) return;

      setCarregandoModelosPreventiva(true);
      try {
        const ref = collection(db, "preventivasModelos");
        const q = query(ref, where("grupoId", "==", usuario.grupoId));
        const snap = await getDocs(q);
        const lista = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => String(a.nome || "").localeCompare(String(b.nome || ""), "pt-BR"));
        setModelosPreventiva(lista);
      } catch (e) {
        console.error("Erro ao carregar modelos de preventiva:", e);
      } finally {
        setCarregandoModelosPreventiva(false);
      }
    }

    carregarModelosPreventiva();
  }, [usuario]);

  useEffect(() => {
    async function carregarChecklistsInstalacao() {
      if (!usuario?.grupoId) return;

      setCarregandoChecklistsInstalacao(true);
      try {
        const ref = collection(db, "checklistsModelos");
        const q = query(ref, where("grupoId", "==", usuario.grupoId), where("tipo", "==", "INSTALACAO"));
        const snap = await getDocs(q);
        const lista = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => String(a.nome || "").localeCompare(String(b.nome || ""), "pt-BR"));
        setChecklistsInstalacao(lista);
      } catch (e) {
        console.error("Erro ao carregar checklists de instalacao:", e);
      } finally {
        setCarregandoChecklistsInstalacao(false);
      }
    }

    carregarChecklistsInstalacao();
  }, [usuario]);

  const modelosPreventivaOrdenados = useMemo(() => {
    const lista = Array.isArray(modelosPreventiva) ? [...modelosPreventiva] : [];
    const marcaN = String(marca || "").trim().toLowerCase();
    const modeloN = String(modelo || "").trim().toLowerCase();

    const score = (m) => {
      const fab = String(m?.fabricante || "").trim().toLowerCase();
      const mod = String(m?.modelo || "").trim().toLowerCase();
      let s = 0;
      if (marcaN && fab === marcaN) s += 2;
      if (modeloN && mod === modeloN) s += 2;
      if (marcaN && modeloN && fab === marcaN && mod === modeloN) s += 5;
      return s;
    };

    lista.sort((a, b) => {
      const sa = score(a);
      const sb = score(b);
      if (sa !== sb) return sb - sa; // maior score primeiro
      return String(a.nome || "").localeCompare(String(b.nome || ""), "pt-BR");
    });

    return lista;
  }, [modelosPreventiva, marca, modelo]);

  const modelosPreventivaFiltrados = useMemo(() => {
    const t = String(filtroModeloPreventiva || "").trim().toLowerCase();
    if (!t) return modelosPreventivaOrdenados;
    return modelosPreventivaOrdenados.filter((m) => {
      const campos = [m.codigo, m.nome, m.fabricante, m.modelo, m.periodicidade];
      return campos.some((c) => String(c || "").toLowerCase().includes(t));
    });
  }, [modelosPreventivaOrdenados, filtroModeloPreventiva]);


  async function getNextCodigoSequencial(grupoId) {
    const contadorRef = doc(db, "contadores", `equipamentos_${grupoId}`);

    return await runTransaction(db, async (tx) => {
      const snap = await tx.get(contadorRef);
      if (!snap.exists()) {
        tx.set(
          contadorRef,
          { grupoId, seq: 1, atualizadoEm: serverTimestamp() },
          { merge: true }
        );
        return 1;
      }
      const atual = Number(snap.data()?.seq || 0);
      const novo = atual + 1;
      tx.set(contadorRef, { seq: novo, atualizadoEm: serverTimestamp() }, { merge: true });
      return novo;
    });
  }


  async function resolverClienteParaSalvar() {
    if (!modoGlobal && empresaAtual) {
      return {
        empresaId: empresaAtual.id || null,
        empresaNome: empresaAtual.nome || "",
      };
    }

    if (precisaSelecionarCliente) {
      if (!clienteId) throw new Error("Selecione o cliente.");
      const ref = doc(db, "empresas", clienteId);
      const snap = await getDoc(ref);
      if (!snap.exists()) throw new Error("Cliente não encontrado.");
      return { empresaId: clienteId, empresaNome: snap.data()?.nome || "" };
    }

    return { empresaId: null, empresaNome: "" };
  }

  function anexarFotos(files) {
    const arr = Array.from(files || []);
    if (arr.length === 0) return;

    const novos = arr.map((file) => ({
      id: uid(),
      kind: "new",
      file,
      previewUrl: URL.createObjectURL(file),
    }));

    setFotos((prev) => [...prev, ...novos]);
  }

  function removerFoto(id) {
    setFotos((prev) => {
      const item = prev.find((x) => x.id === id);
      if (item?.previewUrl) {
        try {
          URL.revokeObjectURL(item.previewUrl);
        } catch {}
      }
      return prev.filter((x) => x.id !== id);
    });
  }

  async function handleSalvar(e) {
    e.preventDefault();

    if (!usuario?.grupoId) {
      setErro("Usuário sem grupo. Verifique seu cadastro.");
      return;
    }

    if (!nome.trim()) {
      setErro("Informe o nome do equipamento.");
      return;
    }

    if (checklistInstalacaoAtiva && !checklistInstalacaoId) {
      setErro("Selecione o checklist de instalacao.");
      return;
    }

    const checklistInstalacaoSelecionado = checklistsInstalacao.find((c) => c.id === checklistInstalacaoId) || null;
    if (checklistInstalacaoAtiva && !checklistInstalacaoSelecionado) {
      setErro("Checklist de instalacao invalido. Selecione um checklist existente.");
      return;
    }

if (empresaIdParaAcademias && academias.length > 0 && !academiaId) {
  setErro("Selecione a academia.");
  return;
}

    setErro("");
    setSalvando(true);

    try {
      const { empresaId, empresaNome } = await resolverClienteParaSalvar();
      const seq = await getNextCodigoSequencial(usuario.grupoId);
      const codigo = `EQ${String(seq).padStart(4, "0")}`;

      // Upload das fotos (se houver)
      const novasFotos = fotos.filter((f) => f.kind === "new" && f.file);
      const fotosUrls = [];
      for (let i = 0; i < novasFotos.length; i++) {
        const f = novasFotos[i];
        const url = await uploadToStorage({
          grupoId: usuario.grupoId,
          empresaId: empresaId || "sem_cliente",
          pasta: "equipamentos",
          file: f.file,
          filenamePrefix: `${codigo}_foto${String(i + 1).padStart(2, "0")}`,
        });
        fotosUrls.push(url);
      }

      const academiaSelecionada = academias.find((a) => a.id === academiaId) || null;
      const contratoTipoRaw = String(
        academiaSelecionada?.tipoContrato || academiaSelecionada?.tipo || "Avulso"
      ).trim();
      const contratoTipo = contratoTipoRaw || "Avulso";
      const contratoAtivo = !String(contratoTipo).toLowerCase().includes("avulso");

      const docData = {
        // identificação
        codigoSequencial: seq,
        codigo,

        // dados do equipamento
        nome: nome.trim(),
        tipo: (tipo || "").trim(),
        marca: (marca || "").trim(),
        modelo: (modelo || "").trim(),
        numeroSerie: (numeroSerie || "").trim(),
        cor: (cor || "").trim(),
        instaladoEm: instaladoEm || "",
        montadoPor: (montadoPor || "").trim(),
        condicoesIniciais: Number(condicoesIniciais) || 3,
        descricao: (descricao || "").trim(),

        // fotos
        fotosUrls,
        fotoUrl: fotosUrls?.[0] || "", // capa/compatibilidade

        // escopo
        grupoId: usuario.grupoId,
        empresaId: empresaId || null,
        empresaNome: empresaNome || "",

        setorId: academiaId || null,
        setorNome: academiaSelecionada?.nome || "",
        setorCodigo: academiaSelecionada?.codigo || "",
        setorContratoTipo: contratoTipo,


        // ✅ vinculo de modelos de preventivas no equipamento
        preventivasModeloIds: Array.isArray(modeloPreventivaIds) ? modeloPreventivaIds : [],

        // ✅ checklist de instalacao
        checklistInstalacaoAtiva: !!checklistInstalacaoAtiva,
        checklistInstalacaoId: checklistInstalacaoAtiva ? checklistInstalacaoId || null : null,
        checklistInstalacaoNome: checklistInstalacaoAtiva ? (checklistInstalacaoSelecionado?.nome || "") : "",

        criadoEm: serverTimestamp(),
        criadoPor: usuario.uid || null,
        atualizadoEm: serverTimestamp(),
      };

      const maquinaRef = doc(collection(db, "maquinas"));
      const batch = writeBatch(db);

      batch.set(maquinaRef, docData);

      const modelosSelecionados = (Array.isArray(modelosPreventiva) ? modelosPreventiva : []).filter((m) =>
        (Array.isArray(modeloPreventivaIds) ? modeloPreventivaIds : []).includes(m.id)
      );

      // Instancia preventivas para este equipamento
      modelosSelecionados.forEach((m) => {
        const periodicidadeModelo = m.periodicidade || inferirPeriodicidadeItens(m.itens);
        const prevRef = doc(collection(db, "preventivas"));
        const proximaData = contratoAtivo
          ? calcularProximaDataCamadas({ contratoTipo }, new Date())
          : null;
        const contratoInicioEm = contratoAtivo ? proximaData : null;
        batch.set(prevRef, {
          grupoId: usuario.grupoId,

          empresaId: empresaId || "",
          empresaNome: empresaNome || "",

          maquinaId: maquinaRef.id,
          maquinaNome: docData.nome,

          setorId: docData.setorId || null,
          setorNome: docData.setorNome || "",
          setorCodigo: docData.setorCodigo || "",

          // origem/modelo
          modeloPreventivaId: m.id,
          modeloPreventivaCodigo: m.codigo || "",

          // dados do modelo copiados para a instância
          codigo: m.codigo || "",
          nome: montarNomePreventiva(m.nome || "", periodicidadeModelo),
          fabricante: m.fabricante || "",
          modelo: m.modelo || "",
          periodicidade: periodicidadeModelo || "",
          diaSemana: m.diaSemana ?? null,
          diaMes: m.diaMes ?? null,
          itens: Array.isArray(m.itens) ? m.itens : [],

          contratoTipo,
          contratoInicioEm,
          proximaData,
          ativo: contratoAtivo,

          criadoEm: serverTimestamp(),
          criadoPor: usuario.uid || "",
          atualizadoEm: serverTimestamp(),
        });
      });

      await batch.commit();
      navigate("/cadastros/equipamentos");
    } catch (err) {
      console.error(err);
      setErro(err?.message || "Erro ao salvar equipamento.");
    } finally {
      setSalvando(false);
    }
  }

  const Layout = isMobile ? LayoutMobile : LayoutDesktop;

  return (
    <Layout
      tema={tema}
      alterarTema={alterarTema}
      titulo={isMobile ? "Novo Equipamento" : "NOVO EQUIPAMENTO"}
      subtitulo={"Cadastros / Equipamentos / Novo"}
      abaBottom={isMobile ? "cadastros" : undefined}
    >

      <section style={{ width: "100%" }}>
        <div
          className={isMobile ? "m-card" : "pc-card"}
          style={{
            width: "100%",
            padding: isMobile ? 16 : "16px 40px",
            boxSizing: "border-box",
          }}
        >
          {!isMobile && (
            <header className="pc-table-header">
              <div className="pc-table-header-text">
                <h3>Cadastro de equipamento</h3>
                <p>Cadastre um equipamento vinculado ao cliente atual.</p>
              </div>
            </header>
          )}

          {erro ? (
          <div className="pc-alert pc-alert-erro">{erro}</div>
        ) : null}

<form
          onSubmit={handleSalvar}
          className="pc-form"
          style={{
            marginTop: isMobile ? 0 : 12,
            maxWidth: isMobile ? "100%" : 720,
            marginLeft: "auto",
            marginRight: "auto",
          }}
        >
          <div className="pc-form-grid">
            {precisaSelecionarCliente ? (
              <div className="pc-form-group pc-form-group-full">
                <label>Cliente *</label>
                <select value={clienteId} onChange={(e) => setClienteId(e.target.value)} className="pc-input" required>
                  <option value="">Selecione o cliente</option>
                  {clientes.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.nome}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}

<div className="pc-form-group pc-form-group-full">
  <label>Academia{empresaIdParaAcademias && academias.length > 0 ? " *" : ""}</label>
  <select
    value={academiaId}
    onChange={(e) => setAcademiaId(e.target.value)}
    className="pc-input"
    disabled={!empresaIdParaAcademias || carregandoAcademias}
  >
    <option value="">
      {!empresaIdParaAcademias
        ? "Selecione o cliente primeiro"
        : carregandoAcademias
        ? "Carregando..."
        : academias.length === 0
        ? "Nenhuma academia cadastrada"
        : "Selecione a academia"}
    </option>
    {academias.map((a) => (
      <option key={a.id} value={a.id}>
        {a.nome}
      </option>
    ))}
  </select>
  {empresaIdParaAcademias && !carregandoAcademias && academias.length === 0 ? (
    <small style={{ fontSize: 12, opacity: 0.85 }}>
      Cadastre a academia em Cadastros / Academias para poder vincular o equipamento.
    </small>
  ) : null}
</div>

            <div className="pc-form-group pc-form-group-full">
              <label>Nome *</label>
              <input
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                className="pc-input"
                placeholder="Ex: Esteira X100"
              />
            </div>

            <div className="pc-form-group">
              <label>Tipo</label>
              <select value={tipo} onChange={(e) => setTipo(e.target.value)} className="pc-input">
                <option value="">Selecione</option>
                {TIPO_OPTIONS.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>

            <div className="pc-form-group">
              <label>Marca</label>
              <input
                value={marca}
                onChange={(e) => setMarca(e.target.value)}
                className="pc-input"
                placeholder="Ex: Movement"
              />
            </div>

            {/* abaixo de Marca */}
            <div className="pc-form-group">
              <label>Modelo</label>
              <input
                value={modelo}
                onChange={(e) => setModelo(e.target.value)}
                className="pc-input"
                placeholder="Ex: X100 / ProLine"
              />
            </div>

            {/* preventivas (modelos) */}
            <div className="pc-form-group pc-form-group-full">
              <label>Preventivas (modelos)</label>

              <input
                value={filtroModeloPreventiva}
                onChange={(e) => setFiltroModeloPreventiva(e.target.value)}
                className="pc-input"
                placeholder="Buscar por código, nome, fabricante, modelo..."
                style={{ marginBottom: 8 }}
              />

              <div
                style={{
                  border: "1px solid #334155",
                  borderRadius: 10,
                  padding: 10,
                  maxHeight: 240,
                  overflow: "auto",
                }}
              >
                {carregandoModelosPreventiva ? (
                  <div style={{ fontSize: 13, opacity: 0.9 }}>Carregando modelos...</div>
                ) : modelosPreventivaFiltrados.length === 0 ? (
                  <div style={{ fontSize: 13, opacity: 0.9 }}>
                    Nenhum modelo encontrado. Cadastre em <b>Cadastros / Preventivas</b>.
                  </div>
                ) : (
                  modelosPreventivaFiltrados.map((m) => {
                    const checked = modeloPreventivaIds.includes(m.id);
                    const titulo = `${m.codigo ? `${m.codigo} — ` : ""}${m.nome || "Preventiva"}`;
                    const subt = `${m.fabricante || "-"} • ${m.modelo || "-"} • ${m.periodicidade || "-"}`;
                    return (
                      <label
                        key={m.id}
                        style={{
                          display: "flex",
                          gap: 10,
                          alignItems: "flex-start",
                          padding: "8px 6px",
                          borderBottom: "1px dashed rgba(148,163,184,0.35)",
                          cursor: "pointer",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => {
                            setModeloPreventivaIds((prev) => {
                              if (prev.includes(m.id)) return prev.filter((x) => x !== m.id);
                              return [...prev, m.id];
                            });
                          }}
                          style={{ marginTop: 3 }}
                        />
                        <div style={{ lineHeight: 1.2 }}>
                          <div style={{ fontWeight: 700, fontSize: 13 }}>{titulo}</div>
                          <div style={{ fontSize: 12, opacity: 0.85 }}>{subt}</div>
                        </div>
                      </label>
                    );
                  })
                )}
              </div>

              <small style={{ fontSize: 12, opacity: 0.85 }}>
                Ao salvar o equipamento, o sistema cria automaticamente as preventivas deste equipamento com a primeira{" "}
                <b>próxima data</b> calculada pela regra do modelo. No futuro, o módulo de contratos poderá reajustar as datas.
              </small>
            </div>

            {/* checklist de instalacao */}
            <div className="pc-form-group pc-form-group-full">
              <label>Checklist de instalacao</label>
              <label style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 6 }}>
                <input
                  type="checkbox"
                  checked={checklistInstalacaoAtiva}
                  onChange={(e) => {
                    const ativo = e.target.checked;
                    setChecklistInstalacaoAtiva(ativo);
                    if (!ativo) setChecklistInstalacaoId("");
                  }}
                />
                Usar checklist de instalacao
              </label>

              {checklistInstalacaoAtiva ? (
                <select
                  className="pc-input"
                  value={checklistInstalacaoId}
                  onChange={(e) => setChecklistInstalacaoId(e.target.value)}
                  disabled={carregandoChecklistsInstalacao}
                  style={{ marginTop: 8 }}
                >
                  <option value="">
                    {carregandoChecklistsInstalacao
                      ? "Carregando..."
                      : checklistsInstalacao.length === 0
                      ? "Nenhum checklist encontrado"
                      : "Selecione o checklist"}
                  </option>
                  {checklistsInstalacao.map((c) => (
                    <option key={c.id} value={c.id}>
                      {`${c.codigo || "-"} — ${c.nome || "Checklist"}`}
                    </option>
                  ))}
                </select>
              ) : null}

              <small style={{ fontSize: 12, opacity: 0.85 }}>
                Cadastre checklists em <b>Cadastros / Checklists</b> (tipo Instalacao).
              </small>
            </div>


            <div className="pc-form-group">
              <label>Número de série / Rastreabilidade</label>
              <input
                value={numeroSerie}
                onChange={(e) => setNumeroSerie(e.target.value)}
                className="pc-input"
                placeholder="Ex: SN123456 / Patrimônio"
              />
            </div>

            <div className="pc-form-group">
              <label>Cor</label>
              <input
                value={cor}
                onChange={(e) => setCor(e.target.value)}
                className="pc-input"
                placeholder="Ex: Preto"
              />
            </div>

            <div className="pc-form-group">
              <label>Instalado</label>
              <input type="date" value={instaladoEm} onChange={(e) => setInstaladoEm(e.target.value)} className="pc-input" />
            </div>

            <div className="pc-form-group">
              <label>Montado por</label>
              <input
                value={montadoPor}
                onChange={(e) => setMontadoPor(e.target.value)}
                className="pc-input"
                placeholder="Ex: Equipe interna"
              />
            </div>

            <div className="pc-form-group">
              <label>Condições iniciais (1 a 5)</label>
              <select
                value={condicoesIniciais}
                onChange={(e) => setCondicoesIniciais(Number(e.target.value))}
                className="pc-input"
              >
                {[1, 2, 3, 4, 5].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
              <small style={{ fontSize: 12, opacity: 0.85 }}>1 = ruim | 5 = excelente</small>
            </div>

            {/* Foto (opcional) - modelo igual NovaOS */}
            <div className="pc-form-group pc-form-group-full">
              <label>Foto (opcional)</label>

              <div style={{ marginTop: 6, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                <button
                  type="button"
                  className="pc-btn-secundario"
                  disabled={salvando}
                  onClick={() => inputFotoCameraRef.current?.click()}
                >
                  📷 Câmera
                </button>

                <button
                  type="button"
                  className="pc-btn-secundario"
                  disabled={salvando}
                  onClick={() => inputFotoArquivoRef.current?.click()}
                >
                  ⬆️ Arquivo
                </button>
              </div>

              {/* Inputs escondidos */}
              <input
                ref={inputFotoCameraRef}
                type="file"
                accept="image/*"
                capture="environment"
                multiple
                style={{ display: "none" }}
                onChange={(e) => {
                  anexarFotos(e.target.files);
                  if (e.target) e.target.value = "";
                }}
              />
              <input
                ref={inputFotoArquivoRef}
                type="file"
                accept="image/*"
                multiple
                style={{ display: "none" }}
                onChange={(e) => {
                  anexarFotos(e.target.files);
                  if (e.target) e.target.value = "";
                }}
              />

              {/* Preview */}
              {fotos.length > 0 ? (
                <div style={{ marginTop: 10, display: "flex", gap: 10, flexWrap: "wrap" }}>
                  {fotos.map((item) => (
                    <div
                      key={item.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        padding: 8,
                        borderRadius: 10,
                        border: "1px solid var(--cor-borda-input, #334155)",
                      }}
                    >
                      <img
                        src={item.previewUrl}
                        alt=""
                        style={{
                          width: 52,
                          height: 52,
                          objectFit: "cover",
                          borderRadius: 8,
                          border: "1px solid var(--cor-borda-input, #334155)",
                        }}
                      />
                      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                        <a href={item.previewUrl} target="_blank" rel="noreferrer" style={{ fontSize: 12, opacity: 0.9 }}>
                          Abrir
                        </a>
                        <button
                          type="button"
                          className="pc-btn-perigo"
                          style={{ padding: "4px 8px", fontSize: 12 }}
                          onClick={() => removerFoto(item.id)}
                        >
                          Remover
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : null}

              <small style={{ fontSize: 12, opacity: 0.85, display: "block", marginTop: 8 }}>
                As fotos serão enviadas ao salvar o equipamento.
              </small>
            </div>

            <div className="pc-form-group pc-form-group-full">
              <label>Descrição</label>
              <textarea
                value={descricao}
                onChange={(e) => setDescricao(e.target.value)}
                className="pc-input"
                placeholder="Observações, detalhes do estado inicial, etc."
                rows={5}
              />
            </div>
          </div>

          <div className="pc-form-actions">
            <button
              type="button"
              className="pc-btn-secundario"
              onClick={() => navigate("/cadastros/equipamentos")}
              disabled={salvando}
            >
              Cancelar
            </button>

            <button type="submit" className="pc-btn-primario" disabled={salvando}>
              {salvando ? "Salvando..." : "Salvar"}
            </button>
          </div>
        </form>
        </div>
      </section>
</Layout>
  );
}

export default NovaMaquina;
